import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Clock, Moon, Sun, Coffee, Zap, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
  } catch { return iso; }
}
function countdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "now";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `in ${h}h ${m}m`;
}

export default function ShiftDashboard({ plan, stats, onGoto }) {
  const [brief, setBrief] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingBrief(true);
    http.get("/ai/daily-brief").then((r) => { if (!cancelled) setBrief(r.data?.brief); }).catch(() => {}).finally(() => { if (!cancelled) setLoadingBrief(false); });
    return () => { cancelled = true; };
  }, []);

  const nextShift = plan?.next_shift;
  const nextSleep = plan?.next_sleep;
  const sleepDebt = stats?.sleep_debt ?? 0;
  const avgQ = stats?.avg_quality ?? 0;
  const avgH = stats?.avg_hours ?? 0;
  const circadianScore = Math.max(0, Math.min(100, Math.round(60 + avgQ * 8 - sleepDebt * 3)));

  return (
    <section className="space-y-6">
      {/* Hero */}
      <div className="circa-card p-6 sm:p-8 relative overflow-hidden">
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            <div className="pill text-indigo-300 border-indigo-500/30 bg-indigo-950/60 mb-4">
              <Sparkles className="w-3.5 h-3.5" /> Circa · Your daily brief
            </div>
            {loadingBrief ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4 bg-white/10" />
                <Skeleton className="h-4 w-2/3 bg-white/10" />
                <Skeleton className="h-4 w-1/2 bg-white/10" />
              </div>
            ) : (
              <p className="text-slate-200 text-base sm:text-lg leading-relaxed whitespace-pre-wrap max-w-3xl" data-testid="daily-brief">{brief}</p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() => onGoto?.("calendar")}
                data-testid="btn-add-shift"
                className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"
              >
                Add a shift <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                onClick={() => onGoto?.("log")}
                data-testid="btn-log-sleep"
                variant="outline"
                className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
              >
                Log sleep
              </Button>
              <Button
                onClick={() => onGoto?.("ai")}
                data-testid="btn-ai-ask-question"
                variant="ghost"
                className="rounded-full text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10"
              >
                Ask Circa
              </Button>
            </div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500 mb-3">Circadian Score</div>
            <div className="flex items-end gap-2">
              <div className="font-display text-5xl font-extrabold text-indigo-300" data-testid="metric-circadian-score">{circadianScore}</div>
              <div className="text-slate-400 pb-2">/ 100</div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400" style={{ width: `${circadianScore}%` }} />
            </div>
            <div className="mt-4 text-xs text-slate-400 leading-relaxed">
              Composite of anchor-sleep adherence, quality, and debt over 14 days.
            </div>
          </div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard testid="metric-next-shift" icon={Clock} tint="text-emerald-300" label="Next shift" value={nextShift ? formatWhen(nextShift.start) : "None"} sub={nextShift ? `${nextShift.label} · ${countdown(nextShift.start)}` : "Add one in Shifts"} />
        <MetricCard testid="metric-next-sleep" icon={Moon} tint="text-indigo-300" label="Next anchor sleep" value={nextSleep ? formatWhen(nextSleep.start) : "—"} sub={nextSleep ? `${countdown(nextSleep.start)} · 7.5h target` : "Add a shift first"} />
        <MetricCard testid="metric-sleep-debt" icon={Zap} tint="text-amber-300" label="Sleep debt (7d)" value={`${sleepDebt}h`} sub={sleepDebt > 6 ? "High — prioritize rest" : sleepDebt > 3 ? "Moderate — add a nap" : "Well recovered"} />
        <MetricCard testid="metric-avg-sleep" icon={Sun} tint="text-sky-300" label="Avg sleep (14d)" value={`${avgH}h`} sub={`Quality ${avgQ}/5`} />
      </div>
    </section>
  );
}

function MetricCard({ icon: Icon, label, value, sub, tint, testid }) {
  return (
    <div className="circa-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest font-mono-ui text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${tint}`} />
      </div>
      <div className="font-display text-2xl font-bold text-slate-100">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{sub}</div>
    </div>
  );
}
